import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { extractErrorMessage } from "../../api/client";
import {
  getSizingPreview,
  provisionSearchEnv,
  SizingPreview,
} from "../../api/searchEnvs";
import { CostFormulaPre } from "../searchEnv/CostFormulaPre";

interface Props {
  open: boolean;
  workflowId: string;
  onClose: () => void;
  onProvisioned: (envId: string) => void;
}

// Pre-canned TTL choices in hours. The chip set; users who want
// something else go through the "custom" picker. 90d is the
// operator-side hard cap (_PROVISIONING_TIMEOUT_HOURS_MAX), so it's
// the last chip — anything beyond it 400s at provision time.
const TTL_OPTIONS: { label: string; hours: number }[] = [
  { label: "1h", hours: 1 },
  { label: "4h", hours: 4 },
  { label: "8h", hours: 8 },
  { label: "24h", hours: 24 },
  { label: "7d", hours: 24 * 7 },
  { label: "30d", hours: 24 * 30 },
  { label: "90d", hours: 24 * 90 },
];

// Auto-sizing tier classes the user can pick from in the override
// dropdown. Kept in sync with the operator's _SIZING_TABLE; expand
// here if a new tier lands operator-side. t3.small.search is omitted
// intentionally — AWS-side 429 throttling makes it unreliable for
// real indexing loads.
const INSTANCE_CLASSES = [
  "t3.medium.search",
  "m6g.large.search",
  "m6g.xlarge.search",
];

// Render USD with two decimals; small values keep their detail
// (e.g. $0.04) so the user can see compute being small vs egress.
function fmtUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

export function SearchEnvProvisionModal({
  open,
  workflowId,
  onClose,
  onProvisioned,
}: Props) {
  const [ttlHours, setTtlHours] = useState(4);
  const [useOverride, setUseOverride] = useState(false);
  const [overrideClass, setOverrideClass] = useState(INSTANCE_CLASSES[0]);
  // Override-mode node count. Auto-pick stays single-node — the user
  // must explicitly Override to spread the indexing load across nodes.
  const [overrideNodes, setOverrideNodes] = useState(1);
  // Editable indexing-job deadline. Pre-filled from the preview's
  // suggested_timeout_hours; user can dial up if they expect ingest
  // headwinds. Stored as a string so the input renders cleanly during
  // editing; coerced to int on submit.
  const [timeoutHours, setTimeoutHours] = useState<string>("");
  // True once the user has typed in the timeout input. While false we
  // keep mirroring the preview's suggested_timeout_hours; once they
  // touch it we stop overwriting so their edit isn't clobbered by the
  // next preview refresh.
  const [timeoutDirty, setTimeoutDirty] = useState(false);
  // Editable number_of_shards for the paim-{env_id} index. Same
  // pre-fill / dirty-tracking pattern as timeoutHours. Multi-shard
  // distributes primary writes across cluster nodes; single-shard
  // funnels everything through one regardless of node count.
  const [shards, setShards] = useState<string>("");
  const [shardsDirty, setShardsDirty] = useState(false);

  const [preview, setPreview] = useState<SizingPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Cost-formula reveal. Mirrors the SearchEnvCard pattern: chevron next
  // to the heading + monospace pre block when expanded.
  const [costExpanded, setCostExpanded] = useState(false);

  // Storage size for the override path. Derived from data_size_gib
  // when we have a preview: bulk-load inflation is ~6× (decompress +
  // bulk-envelope), rounded up to 100 GiB increments. Per-node, so
  // multi-node deployments don't double-up the request. Manual
  // control through a future input; for now the derived default is
  // enough.
  const overrideStorageGib = preview
    ? Math.max(
        100,
        Math.ceil((preview.data_size_gib * 6) / overrideNodes / 100) * 100,
      )
    : 100;

  // Re-fetch the preview whenever the modal opens, TTL changes, or
  // the override class changes (so cost reflects the picked tier).
  // The endpoint is cheap; we don't bother debouncing.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError(null);
    const override = useOverride
      ? {
          instance_class: overrideClass,
          instance_count: overrideNodes,
          storage_gib: overrideStorageGib,
        }
      : undefined;
    getSizingPreview(workflowId, ttlHours, override)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        // Auto-flip to override when the auto-pick refused (data
        // above the ceiling). Only the first time we observe it —
        // don't bounce the toggle once the user has explicitly
        // chosen. Also pre-pick the largest tier; the user can dial
        // down via the dropdown if they know better, but the safe
        // default is "biggest" since the auto-pick already rejected
        // every smaller option.
        if (p.recommended_sizing === null && !useOverride) {
          setUseOverride(true);
          setOverrideClass(INSTANCE_CLASSES[INSTANCE_CLASSES.length - 1]);
        }
        // Mirror the suggested timeout until the user touches the
        // input. Once they edit it, ``timeoutDirty`` pins their value.
        if (!timeoutDirty) {
          setTimeoutHours(String(p.suggested_timeout_hours));
        }
        if (!shardsDirty) {
          setShards(String(p.suggested_shards));
        }
      })
      .catch((err) => {
        if (!cancelled) setPreviewError(extractErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    workflowId,
    ttlHours,
    useOverride,
    overrideClass,
    overrideNodes,
    overrideStorageGib,
    timeoutDirty,
    shardsDirty,
  ]);

  const handleProvision = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    const parsedTimeout = parseInt(timeoutHours, 10);
    const timeout =
      Number.isFinite(parsedTimeout) && parsedTimeout >= 1 && parsedTimeout <= 48
        ? parsedTimeout
        : undefined;
    const parsedShards = parseInt(shards, 10);
    const shardCount =
      Number.isFinite(parsedShards) && parsedShards >= 1 && parsedShards <= 24
        ? parsedShards
        : undefined;
    try {
      const resp = await provisionSearchEnv({
        workflow_id: workflowId,
        ttl_hours: ttlHours,
        instance_class: useOverride ? overrideClass : undefined,
        instance_count: useOverride ? overrideNodes : undefined,
        storage_gib: useOverride ? overrideStorageGib : undefined,
        timeout_hours: timeout,
        shards: shardCount,
      });
      onProvisioned(resp.env_id);
      onClose();
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [
    workflowId,
    ttlHours,
    useOverride,
    overrideClass,
    overrideNodes,
    overrideStorageGib,
    timeoutHours,
    shards,
    onProvisioned,
    onClose,
  ]);

  const sizing = preview?.recommended_sizing;
  const breakdown = preview?.cost_breakdown;
  const total = preview?.estimated_cost_usd;
  const explanation = preview?.cost_explanation;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Provision search environment</DialogTitle>
      <DialogContent dividers>
        {previewError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {previewError}
          </Alert>
        )}

        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Source workflow
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
              {workflowId}
            </Typography>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              Data to index
            </Typography>
            {loadingPreview && !preview ? (
              <CircularProgress size={20} sx={{ mt: 1 }} />
            ) : preview ? (
              <Typography variant="body1">
                {preview.data_size_gib.toFixed(2)} GiB compressed
              </Typography>
            ) : (
              <Typography variant="body1">—</Typography>
            )}
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              Cluster size
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={useOverride ? "override" : "recommended"}
                onChange={(_, v) => v && setUseOverride(v === "override")}
              >
                <ToggleButton value="recommended">Recommended</ToggleButton>
                <ToggleButton value="override">Override</ToggleButton>
              </ToggleButtonGroup>
              {!useOverride && sizing && (
                <Typography variant="body2" color="text.secondary">
                  {sizing.instance_class} × {sizing.instance_count},{" "}
                  {sizing.storage_gib} GiB storage
                </Typography>
              )}
              {useOverride && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Select
                    size="small"
                    value={overrideClass}
                    onChange={(e) => setOverrideClass(e.target.value)}
                  >
                    {INSTANCE_CLASSES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary">
                    ×
                  </Typography>
                  <Select
                    size="small"
                    value={overrideNodes}
                    onChange={(e) => setOverrideNodes(Number(e.target.value))}
                  >
                    {[1, 2, 3].map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="body2" color="text.secondary">
                    nodes, {overrideStorageGib} GiB ea.
                  </Typography>
                </Stack>
              )}
            </Stack>
            {preview?.sizing_error && (
              <Alert severity="warning" sx={{ mt: 1 }} icon={false}>
                {preview.sizing_error}
              </Alert>
            )}
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              TTL
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={ttlHours}
              onChange={(_, v) => v && setTtlHours(v)}
              sx={{ mt: 1 }}
            >
              {TTL_OPTIONS.map((opt) => (
                <ToggleButton key={opt.hours} value={opt.hours}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              Indexing timeout
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <TextField
                size="small"
                type="number"
                value={timeoutHours}
                onChange={(e) => {
                  setTimeoutDirty(true);
                  setTimeoutHours(e.target.value);
                }}
                inputProps={{ min: 1, max: 48, step: 1 }}
                sx={{ width: 100 }}
              />
              <Typography variant="body2" color="text.secondary">
                hours. Auto-scaled from data size + nodes; dial up if you
                expect ingest headwinds (max 48h).
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              Index shards
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <TextField
                size="small"
                type="number"
                value={shards}
                onChange={(e) => {
                  setShardsDirty(true);
                  setShards(e.target.value);
                }}
                inputProps={{ min: 1, max: 24, step: 1 }}
                sx={{ width: 100 }}
              />
              <Typography variant="body2" color="text.secondary">
                primaries. Distributes index writes across cluster nodes;
                single-shard funnels every write to one node regardless of
                node count.
              </Typography>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" color="text.secondary">
                Cost Estimate
              </Typography>
              {explanation && (
                <IconButton
                  size="small"
                  onClick={() => setCostExpanded((v) => !v)}
                  aria-label={
                    costExpanded
                      ? "Hide cost breakdown"
                      : "Show cost breakdown"
                  }
                  sx={{ p: 0.25 }}
                >
                  {costExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              )}
            </Stack>
            {breakdown ? (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <CostLine
                  label="Bulk load (one-time)"
                  amount={breakdown.egress_usd}
                  note="egress, sunk on provision"
                />
                <CostLine
                  label={`Compute (${ttlHours}h)`}
                  amount={breakdown.compute_usd}
                />
                <CostLine
                  label={`Storage (${ttlHours}h)`}
                  amount={breakdown.storage_usd}
                />
                <Divider sx={{ my: 0.5 }} />
                <CostLine label="Total" amount={total ?? null} bold />
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {loadingPreview ? "calculating…" : "—"}
              </Typography>
            )}
            {costExpanded && explanation && (
              <CostFormulaPre formulaLines={explanation.formula_lines} />
            )}
          </Box>

          <Alert severity="info" icon={false}>
            Provisioning takes 15–30 min. You'll get credentials when it's ready.
          </Alert>

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleProvision}
          disabled={
            submitting ||
            loadingPreview ||
            !preview ||
            (!useOverride && !preview.recommended_sizing) ||
            (useOverride && !overrideClass)
          }
        >
          {submitting ? <CircularProgress size={18} /> : "Provision"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CostLine({
  label,
  amount,
  note,
  bold,
}: {
  label: string;
  // Null when the preview lacks the sizing needed to price this line
  // (compute / storage / total in the "above ceiling, no override"
  // case). Egress is never null.
  amount: number | null;
  note?: string;
  bold?: boolean;
}) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Box>
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: bold ? 600 : 400 }}
        >
          {label}
        </Typography>
        {note && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="span"
            sx={{ ml: 1 }}
          >
            ({note})
          </Typography>
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontWeight: bold ? 600 : 400 }}
      >
        {amount === null ? "—" : fmtUsd(amount)}
      </Typography>
    </Stack>
  );
}
